// frontend/src/pages/PayrollDrafts/components/PayRunRow.tsx
// One <tr> for a pay run in the list.
// Owns row presentation only; all actions bubble up via callbacks.

import React from "react";
import { MoreVertical, Play, Eye, Trash2 } from "lucide-react";
import type { PayRun } from "../types";
import { fmtDate, fmtMoney } from "../helpers";
import { C, TABULAR, tdStyle, kebabItemStyle } from "../styles";

interface PayRunRowProps {
  payRun: PayRun;
  isKebabOpen: boolean;
  onOpenRun: () => void;
  onToggleKebab: () => void;
  onCloseKebab: () => void;
  onRequestDelete: () => void;
}

export default function PayRunRow({
  payRun: r,
  isKebabOpen,
  onOpenRun,
  onToggleKebab,
  onCloseKebab,
  onRequestDelete,
}: PayRunRowProps): React.ReactElement {
  const isDraft = r.status === "draft";

  const statusColor = isDraft ? C.amber : C.brandDark;
  const statusBg = isDraft ? C.amberBg : C.brandBg;
  const statusLabel = isDraft ? "Draft" : "Finalized";

  return (
    <tr style={{ borderBottom: "1px solid " + C.lineSoft }}>
      <td style={{ ...tdStyle, cursor: "pointer" }} onClick={onOpenRun}>
        {fmtDate(r.pay_period_start)} to {fmtDate(r.pay_period_end)}
      </td>

      <td style={{ ...tdStyle, ...TABULAR, cursor: "pointer" }} onClick={onOpenRun}>
        {fmtDate(r.pay_date)}
      </td>

      <td style={{ ...tdStyle, textAlign: "right", ...TABULAR, cursor: "pointer" }} onClick={onOpenRun}>
        {r.employee_count || 0}
      </td>

      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, ...TABULAR, cursor: "pointer" }} onClick={onOpenRun}>
        {fmtMoney(r.total_net)}
      </td>

      <td style={{ ...tdStyle, cursor: "pointer" }} onClick={onOpenRun}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            color: statusColor,
            background: statusBg,
            padding: "3px 10px",
            borderRadius: 5,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          <span style={{ width: 6, height: 6, background: statusColor, borderRadius: "50%" }} />
          {statusLabel}
        </span>
      </td>

      <td
        style={{ ...tdStyle, textAlign: "right", position: "relative", width: 60 }}
        className="row-kebab"
      >
        <button
          onClick={(e) => { e.stopPropagation(); onToggleKebab(); }}
          style={{
            width: 30,
            height: 30,
            border: "none",
            background: "transparent",
            borderRadius: 6,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: C.ink,
          }}
        >
          <MoreVertical size={16} />
        </button>

        {isKebabOpen && (
          <div
            className="row-kebab"
            style={{
              position: "absolute",
              top: 40,
              right: 10,
              background: C.card,
              border: "1px solid " + C.line,
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              padding: "6px 0",
              minWidth: 180,
              zIndex: 50,
              textAlign: "left",
            }}
          >
            {isDraft ? (
              <>
                <button
                  onClick={() => { onCloseKebab(); onOpenRun(); }}
                  style={kebabItemStyle}
                >
                  <Play size={14} style={{ color: C.brand }} /> Continue
                </button>
                <div style={{ height: 1, background: C.line, margin: "4px 0" }} />
                <button
                  onClick={() => { onCloseKebab(); onRequestDelete(); }}
                  style={{ ...kebabItemStyle, color: C.err }}
                >
                  <Trash2 size={14} /> Delete draft
                </button>
              </>
            ) : (
              <button
                onClick={() => { onCloseKebab(); onOpenRun(); }}
                style={kebabItemStyle}
              >
                <Eye size={14} style={{ color: C.brand }} /> View
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}